import React, { useRef, useEffect, useContext, useState } from 'react';
import { Container, Row, Col, Button, Modal } from 'reactstrap';
import { HashLink } from 'react-router-hash-link';
import RepoVideo from '../repository/RepoVideo';
import authService from './api-authorization/AuthorizeService';
import NextVideo from './NextVideo';
import { UserContext } from "../App";
import useVdoPlayer from './useVdoPlayer';
import './Video.css';

const Video = (props) => {
  // Refs and state
  const videoDiv = useRef(null);
  const { myClasses } = useContext(UserContext);
  const [hasVideo, setHasVideo] = useState(false);
  const [playerStatus, setPlayerStatus] = useState('初始化中...');
  const [showAuthError, setShowAuthError] = useState(false);
  const vdoPlayer = useVdoPlayer();
  const [metadataTime, setMetadataTime] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    let interval = null;
    if (metadataTime) {
      interval = setInterval(() => {
        const sec = Math.floor((Date.now() - metadataTime) / 1000);
        setElapsed(sec);
        if (sec > 5) {
          clearInterval(interval);
        }
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [metadataTime]);

  // Extract routing params
  let { classId, chapterIdx, sectionIdx, videoIdx } = props.match.params;

  // Resolve the current class/course data
  const targetClass = (myClasses.length && myClasses.length > 0)
    ? myClasses.find((x) => x.id == classId)
    : null;
  const course = targetClass
    ? JSON.parse(targetClass.courseData)
    : { chapters: [{ sections: [{ videos: [{ title: "" }] }] }] };

  // Normalise indices
  chapterIdx = parseInt(chapterIdx, 10);
  sectionIdx = parseInt(sectionIdx, 10);
  videoIdx = parseInt(videoIdx, 10);

  const chapter = course.chapters[chapterIdx];
  const section = chapter.sections[sectionIdx];
  const video = section.videos[videoIdx];

  // Helper to handle video action with auth error handling
  const handleVideoAction = async (videoId, actionId, classId) => {
    try {
      await RepoVideo.sendVideoAction(videoId, actionId, classId);
    } catch (error) {
      if (error.status === 401 || error.status === 403) {
        setShowAuthError(true);
      } else {
        console.error('Video action error:', error);
      }
    }
  };

  // Log page view when a video is present
  if (video) {
    handleVideoAction(video.id, 1, classId);
  }

  // Helper functions to navigate the course structure
  const isLastSection = () => sectionIdx === chapter.sections.length - 1;
  const isLastVideo = () => videoIdx === section.videos.length - 1;
  const isLastChapter = () => chapterIdx === course.chapters.length - 1;

  const nextVideo = () => {
    if (isLastVideo()) {
      if (isLastSection()) {
        if (isLastChapter()) return null;
        return {
          classId,
          chapterIdx: chapterIdx + 1,
          sectionIdx: 0,
          videoIdx: 0,
          title: course.chapters[chapterIdx + 1].sections[0].videos[0].title,
        };
      }
      return {
        classId,
        chapterIdx,
        sectionIdx: sectionIdx + 1,
        videoIdx: 0,
        title: course.chapters[chapterIdx].sections[sectionIdx + 1].videos[0].title,
      };
    }
    return {
      classId,
      chapterIdx,
      sectionIdx,
      videoIdx: videoIdx + 1,
      title: course.chapters[chapterIdx].sections[sectionIdx].videos[videoIdx + 1].title,
    };
  };

  // Load (or reload) the video player
  async function loadVideo() {
    if (!vdoPlayer.isAPIReady) {
      console.log('API not ready yet');
      setPlayerStatus('等待 API 載入...');
      return;
    }

    // Remove any previous iframe to avoid stacking
    if (videoDiv.current) {
      videoDiv.current.innerHTML = '';
    }
    setHasVideo(false);
    setPlayerStatus('準備載入影片...');
    setMetadataTime(null);
    setElapsed(0);

    try {
      setPlayerStatus('取得影片授權中...');
      const { otp, vid } = await RepoVideo.getOtp(video.id);

      setPlayerStatus('建立播放器...');
      // Create iframe via the hook
      const iframe = vdoPlayer.loadVideo({
        otp,
        playbackInfo: btoa(JSON.stringify({ videoId: vid })),
        container: videoDiv.current,
        configuration: { theme: '9111e0056d664282b368c5558a36f32a' },
      });

      // Wrap with VdoPlayer to get HTML5 video events
      const player = new window.VdoPlayer(iframe);

      let registered30 = false;
      let registered50 = false;
      let registered90 = false;
      let metadataTimeout = null;

      player.video.addEventListener('loadstart', () => {
        setPlayerStatus('開始載入影片...');
      });

      player.video.addEventListener('loadedmetadata', () => {
        setPlayerStatus('影片資訊已載入');
        setMetadataTime(Date.now());
        setElapsed(0);
        // Set a 3-second timeout to force ready state if canplay doesn't fire
        metadataTimeout = setTimeout(() => {
          if (!hasVideo) {
            console.log('Forcing video ready state after 3 seconds');
            setHasVideo(true);
            setPlayerStatus('...影片已就緒，可以播放');
          }
        }, 3000);
      });

      player.video.addEventListener('canplay', () => {
        console.log('canplay event called');
        // Clear the timeout since canplay fired normally
        if (metadataTimeout) {
          clearTimeout(metadataTimeout);
          metadataTimeout = null;
        }
        setHasVideo(true);
        setPlayerStatus('影片已就緒，可以播放');
        setMetadataTime(null);
      });

      player.video.addEventListener('play', () => {
        handleVideoAction(video.id, 2, classId);
        console.log('play event called');
        setPlayerStatus('播放中');
        setMetadataTime(null);
      });

      player.video.addEventListener('pause', () => {
        console.log('pause event called');
        setPlayerStatus('已暫停');
      });

      player.video.addEventListener('waiting', () => {
        setPlayerStatus('緩衝中...');
      });

      player.video.addEventListener('playing', () => {
        setPlayerStatus('播放中');
        setMetadataTime(null);
      });

      player.video.addEventListener('ended', () => {
        console.log('ended event called');
        setPlayerStatus('播放完畢');
        if (registered30 && registered50 && registered90) {
          handleVideoAction(video.id, 3, classId);
          handleVideoAction(video.id, 4, classId);
          handleVideoAction(video.id, 5, classId);
          handleVideoAction(video.id, 6, classId);
        } else {
          console.log('Video ended but not all checkpoints reached');
          handleVideoAction(video.id, 7, classId);
        }
      });

      player.video.addEventListener('seeking', () => {
        console.log('seeking event called');
        setPlayerStatus('跳轉中...');
      });

      player.video.addEventListener('seeked', () => {
        setPlayerStatus('播放中');
      });

      player.video.addEventListener('error', (e) => {
        const errorMsg = player.video.error
          ? `錯誤 (code ${player.video.error.code}): ${player.video.error.message || '播放器發生錯誤'}`
          : '播放器發生未知錯誤';
        console.error('Video error:', e, player.video.error);
        setPlayerStatus(errorMsg);
        setMetadataTime(null);
      });

      let ended = false;
      player.video.addEventListener('timeupdate', () => {
        const currentTime = player.video.currentTime;
        const duration = player.video.duration;
        const view_ratio = currentTime / duration;

        if (player.video.ended) {
          if (!ended) {
            ended = true;
            console.log('timeupdate ended-!');
          }
          return;
        }
        if (!registered30 && view_ratio > 0.3 && view_ratio < 0.35) {
          registered30 = true;
          handleVideoAction(video.id, 3, classId);
        }
        if (!registered50 && view_ratio > 0.5 && view_ratio < 0.55) {
          registered50 = true;
          handleVideoAction(video.id, 4, classId);
        }
        if (!registered90 && view_ratio > 0.88 && view_ratio < 0.92) {
          registered90 = true;
          handleVideoAction(video.id, 5, classId);
        }
      });
    } catch (e) {
      console.log(e);
      setPlayerStatus('載入失敗：' + e.message);
      alert('登入逾時、請重新登入系統');
      window.location = '/authentication/login';
    }
  }

  // Reload video when any navigation param changes
  useEffect(() => {
    if (myClasses.length > 0 && vdoPlayer.isAPIReady) {
      loadVideo();
    }
  }, [myClasses.length, vdoPlayer.isAPIReady, classId, chapterIdx, sectionIdx, videoIdx]);

  // Render
  return targetClass == null ? <></> : (
    <Container>
      <Row>
        <Col>
          <h2>{section.name}</h2>
          <h4>{video.title}</h4>
        </Col>
      </Row>
      <Row>
        <Col>
          <div ref={videoDiv} className={`vdo-container ${hasVideo ? 'haveVideo' : ''}`}></div>
        </Col>
      </Row>
      <Row>
        <Col lg={true} className="d-flex align-items-center">
          <HashLink to={`/${classId}#SEC-${chapterIdx}-${sectionIdx}`} className="me-2">
            <Button className="float-left">^ 回課程大綱</Button>
          </HashLink>
          <NextVideo nextVideo={nextVideo()} />
        </Col>
        <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
          狀態：{playerStatus}
          {metadataTime && <span style={{ color: 'grey', marginLeft: '5px' }}>({elapsed > 5 ? '5+' : elapsed}s)</span>}
        </div>
      </Row>

      {/* Authentication Error Modal */}
      <Modal isOpen={showAuthError} toggle={() => setShowAuthError(false)}>
        <div className="modal-header">
          <h5 className="modal-title">登入已過期</h5>
        </div>
        <div className="modal-body">
          <p>您的登入憑證已過期，請重新登入以繼續使用課程平台。</p>
        </div>
        <div className="modal-footer">
          <Button color="primary" onClick={() => { window.location = '/authentication/login'; }}>
            前往登入頁面
          </Button>
        </div>
      </Modal>
    </Container>
  );
};

export { Video };